import React, { useState, useEffect } from 'react';
import { getHeatmap } from '../../services/bobAssistantService';

const OperatingTimeHeatmap = ({ deviceId }) => {
    const [heatmapData, setHeatmapData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tooltip, setTooltip] = useState(null);

    useEffect(() => {
        loadHeatmap();
    }, [deviceId]);

    const loadHeatmap = async () => {
        try {
            setLoading(true);
            const data = await getHeatmap(deviceId);
            setHeatmapData(data.heatmap);
        } catch (err) {
            console.error('Error loading heatmap:', err);
        } finally {
            setLoading(false);
        }
    };

    const getColor = (value, maxValue) => {
        if (value === 0) return 'rgba(255, 255, 255, 0.05)';
        const intensity = Math.min(value / (maxValue || 1), 1);
        return `rgba(99, 102, 241, ${0.2 + intensity * 0.8})`;
    };

    if (loading) {
        return (
            <div className="bob-heatmap">
                <h4>Operating Time Heatmap</h4>
                <p>Loading...</p>
            </div>
        );
    }

    if (!heatmapData) {
        return (
            <div className="bob-heatmap">
                <h4>Operating Time Heatmap</h4>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)' }}>No data available</p>
            </div>
        );
    }

    // Calcular el valor máximo para la escala de colores
    const maxValue = Math.max(
        ...heatmapData.flatMap(day => day.hours.map(h => h.avg_operating_time))
    );

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <div className="bob-heatmap">
            <h4>Operating Time Heatmap</h4>
            <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '16px' }}>
                Visualize machine operation patterns by day and hour
            </p>

            <div className="bob-heatmap-grid">
                {heatmapData.map((day, dayIndex) => (
                    <div key={dayIndex} className="bob-heatmap-row">
                        <span className="bob-heatmap-day-label">{dayNames[day.day]}</span>
                        {day.hours.map((hourData, hourIndex) => (
                            <div
                                key={hourIndex}
                                className="bob-heatmap-cell"
                                style={{
                                    backgroundColor: getColor(hourData.avg_operating_time, maxValue)
                                }}
                                onMouseEnter={(e) => setTooltip({
                                    x: e.clientX,
                                    y: e.clientY,
                                    day: dayNames[day.day],
                                    hour: hourIndex,
                                    operatingTime: hourData.avg_operating_time,
                                    anomaly: hourData.avg_anomaly,
                                    samples: hourData.samples
                                })}
                                onMouseLeave={() => setTooltip(null)}
                            />
                        ))}
                    </div>
                ))}
            </div>

            {/* Labels de horas */}
            <div className="bob-heatmap-hours">
                {Array.from({ length: 24 }, (_, i) => (
                    <span key={i} className="bob-heatmap-hour-label">
                        {i % 4 === 0 ? i : ''}
                    </span>
                ))}
            </div>

            {/* Leyenda */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '16px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.5)'
            }}>
                <span>Less activity</span>
                <div style={{
                    width: '120px',
                    height: '12px',
                    background: 'linear-gradient(to right, rgba(99, 102, 241, 0.2), rgba(99, 102, 241, 1))',
                    borderRadius: '4px'
                }} />
                <span>More activity</span>
            </div>

            {/* Tooltip */}
            {tooltip && (
                <div style={{
                    position: 'fixed',
                    left: tooltip.x + 10,
                    top: tooltip.y + 10,
                    backgroundColor: '#1a1a2e',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '13px',
                    zIndex: 1000,
                    pointerEvents: 'none'
                }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                        {tooltip.day} {tooltip.hour}:00
                    </div>
                    <div>Operating time: {tooltip.operatingTime?.toFixed(1) || 0} min</div>
                    <div>Avg anomaly: {tooltip.anomaly?.toFixed(1) || 0}%</div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                        Samples: {tooltip.samples}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OperatingTimeHeatmap;
